import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowRight,
  CheckCircle,
  Eye,
  EyeSlash,
  LockKey,
  ShieldCheck,
  Sparkle,
} from "@phosphor-icons/react";

const passwordRules = [
  { id: "length", label: "Minimo de 8 caracteres", test: value => value.length >= 8 },
  { id: "number", label: "Pelo menos 1 numero", test: value => /\d/.test(value) },
  { id: "letter", label: "Pelo menos 1 letra", test: value => /[a-zA-Z]/.test(value) },
];

function formatError(detail) {
  if (Array.isArray(detail)) {
    return detail.map(item => item.msg || item.message || String(item)).join(" ");
  }
  if (detail && typeof detail === "object") {
    return detail.message || detail.msg || JSON.stringify(detail);
  }
  return detail ? String(detail) : "Nao foi possivel alterar a senha. Tente novamente.";
}

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, changePassword, demoMode } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const ruleStatus = useMemo(
    () => passwordRules.map(rule => ({ ...rule, valid: rule.test(newPassword) })),
    [newPassword]
  );

  const canSubmit =
    currentPassword.length > 0 &&
    ruleStatus.every(rule => rule.valid) &&
    newPassword === confirmPassword &&
    confirmPassword.length > 0;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");

    if (!canSubmit) {
      setMessage("Confira a senha atual, os requisitos e a confirmacao da nova senha.");
      return;
    }

    setLoading(true);
    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      navigate("/dashboard");
    } catch (err) {
      setMessage(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen overflow-hidden bg-[#f5f7fb] lg:grid-cols-[1.02fr_0.98fr]">
      <section className="relative hidden flex-col justify-between overflow-hidden bg-[#2346ff] p-12 text-white lg:flex">
        <div className="absolute inset-0">
          <div className="absolute inset-8 rounded-[34px] border border-white/10" />
          <div className="absolute left-12 top-28 h-px w-44 bg-white/15" />
          <div className="absolute bottom-24 right-16 h-px w-64 bg-white/15" />
        </div>

        <div className="relative">
          <img src="/schumann-logo-blue-bg.svg" alt="Schumann" className="h-auto w-[220px]" />
          <p className="mt-6 text-xs uppercase tracking-[0.28em] text-white/62">
            Intranet corporativa
          </p>
        </div>

        <div className="relative max-w-[620px]">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/16 bg-white/10 px-3.5 py-2 text-sm text-white/88">
            <Sparkle className="h-4 w-4 text-[#ffd724]" weight="regular" />
            Atualizacao de seguranca
          </div>

          <h1 className="font-heading text-[42px] font-bold leading-[1.08] tracking-normal xl:text-[54px]">
            Crie uma senha definitiva para acessar a intranet
          </h1>

          <p className="mt-6 max-w-lg text-base leading-7 text-white/72">
            Esta etapa aparece no primeiro login ou sempre que a empresa exigir troca de senha.
          </p>
        </div>

        <p className="relative text-sm text-white/45">
          © {new Date().getFullYear()} Schumann. Todos os direitos reservados.
        </p>
      </section>

      <main className="flex min-h-screen items-center justify-center px-5 py-10">
        <div className="w-full max-w-[460px]">
          <div className="mb-7 flex justify-center lg:hidden">
            <img src="/schumann-logo.svg" alt="Schumann" className="h-12 w-auto" />
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-[26px] border border-slate-200/80 bg-white p-6 shadow-[0_18px_48px_rgba(15,23,42,0.08)] sm:p-8"
          >
            <div className="mb-7">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#eef1ff]">
                <ShieldCheck className="h-6 w-6 text-[#2346ff]" weight="regular" />
              </div>

              <h2 className="font-heading text-2xl font-bold text-slate-950">
                Troque sua senha
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                {user?.name ? `${user.name}, ` : ""}por seguranca, defina uma senha nova antes de continuar.
              </p>
            </div>

            {demoMode && (
              <div className="mb-4 rounded-2xl border border-[#2346ff]/15 bg-[#f6f8ff] px-4 py-3 text-xs leading-5 text-slate-600">
                Modo demonstracao ativo: qualquer senha atual preenchida permite testar o fluxo visual.
              </div>
            )}

            {message && (
              <Alert variant="destructive" className="mb-4 rounded-xl py-2">
                <AlertDescription className="text-sm">{message}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword" className="text-sm font-semibold text-slate-700">
                  Senha atual
                </Label>
                <div className="relative">
                  <LockKey className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2346ff]" weight="regular" />
                  <Input
                    id="currentPassword"
                    type={showCurrent ? "text" : "password"}
                    value={currentPassword}
                    onChange={event => setCurrentPassword(event.target.value)}
                    className="h-12 rounded-xl border-slate-200 bg-slate-50 pl-10 pr-11 focus:border-[#2346ff] focus:ring-2 focus:ring-[#2346ff]/20"
                  />
                  <button
                    type="button"
                    aria-label={showCurrent ? "Ocultar senha atual" : "Mostrar senha atual"}
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-slate-400 transition hover:bg-white hover:text-[#2346ff]"
                  >
                    {showCurrent ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-sm font-semibold text-slate-700">
                  Nova senha
                </Label>
                <div className="relative">
                  <LockKey className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2346ff]" weight="regular" />
                  <Input
                    id="newPassword"
                    type={showNew ? "text" : "password"}
                    value={newPassword}
                    onChange={event => setNewPassword(event.target.value)}
                    className="h-12 rounded-xl border-slate-200 bg-slate-50 pl-10 pr-11 focus:border-[#2346ff] focus:ring-2 focus:ring-[#2346ff]/20"
                  />
                  <button
                    type="button"
                    aria-label={showNew ? "Ocultar nova senha" : "Mostrar nova senha"}
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-slate-400 transition hover:bg-white hover:text-[#2346ff]"
                  >
                    {showNew ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-semibold text-slate-700">
                  Confirmar nova senha
                </Label>
                <div className="relative">
                  <LockKey className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2346ff]" weight="regular" />
                  <Input
                    id="confirmPassword"
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={event => setConfirmPassword(event.target.value)}
                    className="h-12 rounded-xl border-slate-200 bg-slate-50 pl-10 pr-11 focus:border-[#2346ff] focus:ring-2 focus:ring-[#2346ff]/20"
                  />
                  <button
                    type="button"
                    aria-label={showConfirm ? "Ocultar confirmacao" : "Mostrar confirmacao"}
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-slate-400 transition hover:bg-white hover:text-[#2346ff]"
                  >
                    {showConfirm ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 p-3">
                <p className="mb-2 text-xs font-bold text-slate-700">Requisitos da senha</p>
                <div className="space-y-1.5">
                  {ruleStatus.map(rule => (
                    <div key={rule.id} className="flex items-center gap-2 text-xs text-slate-500">
                      <CheckCircle className={`h-4 w-4 ${rule.valid ? "text-emerald-500" : "text-slate-300"}`} weight="regular" />
                      {rule.label}
                    </div>
                  ))}
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <CheckCircle className={`h-4 w-4 ${newPassword && newPassword === confirmPassword ? "text-emerald-500" : "text-slate-300"}`} weight="regular" />
                    Senhas iguais
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="h-12 w-full rounded-xl bg-[#2346ff] font-semibold text-white shadow-[0_12px_26px_rgba(35,70,255,0.22)] transition hover:bg-[#1d3ace]"
              >
                {loading ? "Salvando..." : "Salvar nova senha"}
                {!loading && <ArrowRight className="ml-2 h-4 w-4" weight="regular" />}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
